import {
  DangerTriggerSchema, EnemyBehaviourSchema, PlayCompanionSchema, TacticalActionSchema,
  type Continuity, type DangerTrigger, type EnemyBehaviour, type PlayCompanion, type TacticalAction,
} from '@dissidium/contracts';
import { add, canonical, freeze } from '../kernel/primitives.ts';
import { changeRemains, killPerson, validateLedger } from '../continuity/ledger.ts';
import { availableActions, consumeItem, itemDefinition, kitTemplateIdOf, ownedItems } from '../items/index.ts';
import { createContract, type CompanionContract } from '../companions/contracts.ts';
import { decideTactics } from '../ai/tactics.ts';
import { findPath, hasLineOfSight, isWalkable, metricDistance, type NavigationMap, type NavPosition } from '../navigation/index.ts';
import { approach, occupied, opponents, sameCell } from './routing.ts';
import type { AutonomousDecision, CombatActor, CombatState } from './index.ts';

export type CombatSetup = {
  continuity: Continuity;
  enemies?: Readonly<Record<string, EnemyBehaviour>>;
  companions?: Readonly<Record<string, CompanionContract>>;
  dangerOptions?: readonly DangerTrigger[];
};
export type Commitment = {
  action: TacticalAction;
  startedTick: number;
  resolvesTick: number;
  recoveryUntil: number;
};
export type TacticalActor = {
  order: TacticalAction | null;
  commitment: Commitment | null;
  readyTick: number;
  stance: 'holding' | 'braced' | 'mobile';
  braceToward: NavPosition | null;
  intention: string;
  fleeing: boolean;
  escaped: boolean;
};
export type TacticalState = {
  version: 'cb-combat-v1';
  continuity: Continuity;
  enemies: Record<string, EnemyBehaviour>;
  companions: Record<string, PlayCompanion>;
  actors: Record<string, TacticalActor>;
  dangerOptions: DangerTrigger[];
  sighted: string[];
  badlyHurt: string[];
  targetingPc: string[];
};

export function createTactics(state: CombatState, map: NavigationMap, setup: CombatSetup): TacticalState {
  const continuity = validateLedger(setup.continuity);
  const enemies = Object.fromEntries(Object.entries(setup.enemies ?? {}).map(([id, policy]) => [id, EnemyBehaviourSchema.parse(policy)]));
  const companions = Object.fromEntries(Object.entries(setup.companions ?? {}).map(([id, contract]) => [id, PlayCompanionSchema.parse(contract)]));
  const bodies = Object.values(enemies).filter(policy => policy.kind === 'working-dead').map(policy => policy.remainsId);
  if (new Set(bodies).size !== bodies.length) throw new Error('One set of remains cannot drive two combat bodies');
  for (const [id, contract] of Object.entries(companions)) {
    createContract(contract);
    if (contract.companionId !== id || state.actors[id]?.role !== 'companion') throw new Error('Companion agreement must belong to a companion in this encounter');
  }
  for (const [id, policy] of Object.entries(enemies)) {
    if (state.actors[id]?.role !== 'enemy') throw new Error('Enemy behaviour must belong to an enemy');
    if (policy.kind === 'thief' && !isWalkable(map, policy.escape)) throw new Error('Thief escape must be walkable');
    if (policy.kind === 'working-dead') {
      const remains = continuity.remains[policy.remainsId];
      if (!remains || remains.disposition !== 'animated' || continuity.persons[id]) throw new Error('A working body needs animated remains, never a living person identity');
      for (const p of [policy.anchor, policy.task, state.actors[id]!.position]) {
        const distance = metricDistance(policy.anchor, p);
        if (!isWalkable(map, p) || distance === null || distance > policy.radius) throw new Error('Body and task must stay inside the authored tether');
      }
    }
  }
  for (const actor of Object.values(state.actors)) {
    // A spear hit against a retreating target can reach three times base damage.
    if (actor.attackDamage > Math.floor(Number.MAX_SAFE_INTEGER / 3)) throw new Error('Tactical attack damage needs safe integer headroom for exposure');
    const person = continuity.persons[actor.id];
    if (!person && enemies[actor.id]?.kind !== 'working-dead') throw new Error(`Tactical actor ${actor.id} needs a continuity identity`);
    if (person && actor.status === 'alive' && (person.lifecycle !== 'active' || !person.location)) throw new Error('Living combat participants must be manifested, active people');
    if (person && (person.current.hp !== actor.hp || person.current.maxHp !== actor.maxHp || (person.lifecycle === 'dead') !== (actor.status === 'dead'))) throw new Error('Combat health must match the continuity checkpoint');
  }
  const live = Object.values(state.actors).filter(a => a.status === 'alive');
  if (live.some((a, i) => live.slice(i + 1).some(b => sameCell(a.position, b.position)))) throw new Error('Living tactical actors cannot share a cell');
  return {
    version: 'cb-combat-v1', continuity, enemies, companions,
    actors: Object.fromEntries(Object.keys(state.actors).sort().map(id => [id, {
      order: null, commitment: null, readyTick: 0, stance: 'mobile', braceToward: null,
      intention: '', fleeing: false, escaped: false,
    }])),
    dangerOptions: [...new Set((setup.dangerOptions ?? []).map(x => DangerTriggerSchema.parse(x)))].sort(),
    sighted: [], badlyHurt: [], targetingPc: [],
  };
}

/** A bow looses across a room; up close it is a stick. */
const BOW_REACH_CELLS = 10;
const gear = (state: Readonly<CombatState>, id: string): string[] =>
  state.tactics!.continuity.persons[id] ? availableActions(state.tactics!.continuity, id) : [];
const reach = (state: Readonly<CombatState>, actor: CombatActor, map: NavigationMap): number => {
  const actions = gear(state, actor.id);
  if (actions.includes('loose-arrow')) return Math.max(actor.attackRange, map.cellSize * BOW_REACH_CELLS);
  return actor.attackRange;
};
/** How long a blow turned on a braced shield, or swung at someone who stepped out of reach, keeps the striker from striking again. */
const STAGGER_TICKS = 4;
/** Raising a shield to a blow is quicker than swinging one. */
const BRACE_TICKS = 1;
const CUT = (amount: number): number => Math.max(1, Math.floor(amount / 2));
const BRACED = (amount: number): number => Math.max(1, amount - 1);
/** Health one dressing or dose restores, in a fight or out of one. */
export const TREAT_HEALS = 8;
export const HEALING_FACTS = ['dressed-wound', 'prepared-care'];
const isHealingItem = (state: Readonly<CombatState>, id: string, itemId: string): boolean => {
  const item = state.tactics!.continuity.items[itemId];
  const template = item && kitTemplateIdOf(item);
  return !!item && item.allocation.kind === 'person' && item.allocation.id === id && item.units === 1 &&
    !!template && HEALING_FACTS.includes(itemDefinition(template).consumable?.fact ?? '');
};

/** Host affordances are derived from the same inventory checked at execution. */
export function availableCombatOptions(state: Readonly<CombatState>): { actions: TacticalAction['type'][]; consumables: string[] } {
  if (!state.tactics || state.actors[state.pcId]?.status !== 'alive') return { actions: [], consumables: [] };
  const actions: TacticalAction['type'][] = ['engage', 'hold', 'move', 'withdraw', 'retreat'];
  const granted = gear(state, state.pcId);
  if (granted.includes('shield-brace')) actions.push('brace');
  if (granted.includes('thrust')) actions.push('lunge');
  const consumables = healingItems(state, state.pcId);
  if (consumables.length) actions.push('consume');
  return { actions, consumables };
}
/** The unspent dressings and doses this fighter carries. */
export const healingItems = (state: Readonly<CombatState>, id: string): string[] =>
  ownedItems(state.tactics!.continuity, id).filter(item => isHealingItem(state, id, item.id)).map(item => item.id);

/** A shield faced toward `toward` covers a blow coming from `from`. */
const covers = (at: NavPosition, toward: NavPosition | null, from: NavPosition): boolean =>
  !!toward && (from.x - at.x) * (toward.x - at.x) + (from.y - at.y) * (toward.y - at.y) > 0;

/** The first tick an answer given now could start: it breaks off your own wind-up, never your recovery. */
export const answerStart = (state: Readonly<CombatState>, actorId = state.pcId): number => {
  const own = state.tactics!.actors[actorId]!;
  return (own.commitment ? state.tick : Math.max(state.tick, own.readyTick)) + 1;
};

/**
 * What the wind-up line tells you about a blow aimed at you. An answer (lunge,
 * brace, withdraw) is the best change of order that lands in time; the rest say
 * why what you are already doing is better: a short cut is traded, a brace
 * already facing him takes it, a dressing is tied first, your own swing lands
 * first and ends him, an answer is under way, he is too quick on his feet to step
 * away from, or nothing lands in time. Only a
 * heavy blow is answered: a short cut, or a blow that takes less than a third
 * of your health and cannot kill you, is traded. null means the blow is not at
 * you. The auto-pause stops for the answers, and qa/browser/play/guard-plans.ts
 * measures that following them beats plain Attack for every origin's kit.
 */
export type WindupAdvice = 'lunge' | 'brace' | 'withdraw' | 'trade' | 'covered' | 'dressing' | 'finish' | 'answering' | 'quick' | 'late';
export const ANSWERS: readonly WindupAdvice[] = ['lunge', 'brace', 'withdraw'];
export function windupAnswer(state: Readonly<CombatState>, foeId: string, actorId = state.pcId, cellSize = 1000): WindupAdvice | null {
  const tactics = state.tactics, foe = state.actors[foeId];
  const blow = tactics?.actors[foeId]?.commitment;
  if (!tactics || !foe || !blow || (blow.action.type !== 'attack' && blow.action.type !== 'lunge') || blow.action.targetId !== actorId || state.actors[actorId]!.status !== 'alive') return null;
  // Only a heavy blow is worth breaking off for: a short cut, or one that takes less than a third of you and cannot kill you, is traded.
  const self = state.actors[actorId]!, force = strike(state, foe, blow);
  if ((blow.action.type === 'attack' && blow.resolvesTick - blow.startedTick < foe.attackWindupTicks) || (force * 3 < self.maxHp && force < self.hp)) return 'trade';
  const own = tactics.actors[actorId]!, doing = own.commitment?.action.type ?? own.order?.type;
  if (doing === 'lunge' || doing === 'withdraw' || doing === 'retreat') return 'answering';
  // A dressing still being tied is an answer only if it is tied before the blow lands.
  if (doing === 'consume' && (own.commitment?.resolvesTick ?? answerStart(state, actorId) + 4) < blow.resolvesTick) return 'dressing';
  // Your own blow, already swinging, lands first and ends him: finish it.
  const mine = own.commitment;
  if (mine && (mine.action.type === 'attack' || mine.action.type === 'lunge') && mine.action.targetId === foeId && mine.resolvesTick < blow.resolvesTick &&
    receivedForce(state, self, foe, strike(state, self, mine)) >= foe.hp) return 'finish';
  const shield = own.order?.type === 'brace' ? own.order.toward : own.stance === 'braced' ? own.braceToward : null;
  if (covers(self.position, shield, foe.position)) return 'covered';
  const actions = gear(state, actorId), start = answerStart(state, actorId);
  // A spear reaches one cell past a blade (1,000 units at the play host's scale): an archer further off is not lunged at.
  if (actions.includes('thrust') && start + 1 < blow.resolvesTick && (metricDistance(self.position, foe.position) ?? Number.MAX_SAFE_INTEGER) <= self.attackRange + cellSize) return 'lunge';
  if (actions.includes('shield-brace') && start + BRACE_TICKS < blow.resolvesTick) return 'brace';
  // A step back cannot shake a foe quicker on his feet than you, or carry you out of a bow's reach: strike first and keep striking.
  if (foe.movePerTick > self.movePerTick || gear(state, foeId).includes('loose-arrow')) return 'quick';
  return start <= blow.resolvesTick ? 'withdraw' : 'late';
}

export function validateTacticalOrder(state: Readonly<CombatState>, actorId: string, action: TacticalAction): void {
  TacticalActionSchema.parse(action);
  const self = state.actors[actorId];
  if (!state.tactics || !self || self.status === 'dead') throw new Error('A living tactical actor is required');
  if ('targetId' in action) {
    const target = state.actors[action.targetId];
    if (!target || target.status === 'dead' || state.tactics.actors[target.id]!.escaped || (target.role === 'enemy') === (self.role === 'enemy')) throw new Error('Choose a living opponent');
  }
  if (action.type === 'brace' && (!gear(state, actorId).includes('shield-brace') || action.toward.layer !== self.position.layer || sameCell(action.toward, self.position))) throw new Error('Brace requires an equipped shield and a facing on this layer');
  if (action.type === 'lunge' && !gear(state, actorId).includes('thrust')) throw new Error('Lunge requires an equipped spear');
  if (action.type === 'consume' && !isHealingItem(state, actorId, action.itemId)) throw new Error('Choose an owned, unspent healing consumable');
}

function stance(state: CombatState, actor: CombatActor, kind: TacticalActor['stance'], toward: NavPosition | null = null): void {
  const own = state.tactics!.actors[actor.id]!;
  if (own.stance === kind && canonical(own.braceToward) === canonical(toward)) return;
  own.stance = kind; own.braceToward = toward;
  state.events.push({ type: 'stance', tick: state.tick, actorId: actor.id, stance: kind, toward });
}

function end(state: CombatState, actor: CombatActor, action: TacticalAction, outcome: 'resolved' | 'blocked' | 'interrupted' | 'unavailable', reason: string): void {
  state.tactics!.actors[actor.id]!.commitment = null;
  state.events.push({ type: 'action-ended', tick: state.tick, actorId: actor.id, action, outcome, reason });
}

function nextAction(state: CombatState, map: NavigationMap, actor: CombatActor): TacticalAction | null {
  const own = state.tactics!.actors[actor.id]!;
  const order = own.order;
  if (!order) return null;
  try { validateTacticalOrder(state, actor.id, order); }
  catch (error) {
    own.order = null;
    end(state, actor, order, 'unavailable', error instanceof Error ? error.message : 'Action unavailable');
    return null;
  }
  if (order.type === 'hold' || order.type === 'brace') {
    if (order.type === 'hold' && own.stance !== 'holding') return order;
    if (order.type === 'brace' && (own.stance !== 'braced' || !sameCell(own.braceToward!, order.toward))) return order;
    const target = opponents(state, actor).find(foe => (metricDistance(actor.position, foe.position) ?? Number.MAX_SAFE_INTEGER) <= reach(state, actor, map) && hasLineOfSight(map, actor.position, foe.position));
    // A duty named against this foe also bars defensive strikes; a general
    // no-charge duty does not stop a companion striking what reaches them.
    const contract = state.tactics!.companions[actor.id];
    if (target && contract?.obligations.some(o => o.forbiddenGoals.includes('engage') && o.against?.includes(target.id))) return null;
    return target ? { type: 'attack', targetId: target.id } : null;
  }
  if (order.type === 'engage') {
    const target = state.actors[order.targetId]!;
    const range = reach(state, actor, map);
    if ((metricDistance(actor.position, target.position) ?? Number.MAX_SAFE_INTEGER) <= range && hasLineOfSight(map, actor.position, target.position)) return { type: 'attack', targetId: target.id };
    const step = approach(state, map, actor, target, range);
    return step ? { type: 'move', destination: step.position } : null;
  }
  if ('destination' in order) {
    if (sameCell(actor.position, order.destination)) { own.order = null; return null; }
    const route = findPath(map, actor.position, order.destination, occupied(state, actor.id));
    if (!route?.length) { own.order = null; end(state, actor, order, 'blocked', 'The route is blocked.'); return null; }
    return { type: order.type, destination: route[0]!.position };
  }
  return order;
}

function begin(state: CombatState, map: NavigationMap, actor: CombatActor, action: TacticalAction): void {
  const own = state.tactics!.actors[actor.id]!;
  const body = state.tactics!.enemies[actor.id];
  if ('destination' in action && body?.kind === 'working-dead' && (metricDistance(body.anchor, action.destination) ?? Number.MAX_SAFE_INTEGER) > body.radius) return;
  if (!(action.type === 'attack' && (own.order?.type === 'brace' || own.order?.type === 'hold'))) stance(state, actor, 'mobile');
  let windup = 2;
  let recovery = 1;
  if (action.type === 'attack') {
    windup = Math.max(2, actor.attackWindupTicks); recovery = 2;
    // A trained guard mixes his blows: every second one is a short cut, too quick
    // for a lunge to catch, so a spear answers only his full swings. A bow has no short cut.
    const policy = state.tactics!.enemies[actor.id];
    const blows = state.events.filter(e => e.type === 'action-started' && e.actorId === actor.id && e.action.type === 'attack').length;
    if ((policy?.kind === 'guard' || policy?.kind === 'survivor') && policy.mixedBlows && blows % 2 === 1 && !gear(state, actor.id).includes('loose-arrow')) windup = 2;
  }
  // A lunge breaks a wind-up, but the long recovery lets the broken foe swing again before the next one: no lunge-lock.
  if (action.type === 'lunge') { windup = 1; recovery = 6; }
  if (action.type === 'brace') { windup = BRACE_TICKS; recovery = 1; }
  if (action.type === 'consume') { windup = 4; recovery = 2; }
  if ('destination' in action) {
    const route = findPath(map, actor.position, action.destination);
    const cost = route?.[0]?.cost ?? map.cellSize;
    windup = Math.max(1, Math.ceil(cost / actor.movePerTick)) * (action.type === 'withdraw' ? 2 : 1);
    recovery = 0;
  }
  if (body?.kind === 'working-dead') windup = Math.max(4, windup * 2);
  const resolvesTick = add(state.tick, windup);
  const recoveryUntil = add(resolvesTick, recovery);
  own.commitment = { action: structuredClone(action), startedTick: state.tick, resolvesTick, recoveryUntil };
  own.readyTick = recoveryUntil;
  state.events.push({ type: 'action-started', tick: state.tick, actorId: actor.id, action: structuredClone(action), resolvesTick, recoveryUntil });
}

/** Predict and apply the same present defence; a shielded foe may survive an apparently killing counterstroke. */
function receivedForce(state: Readonly<CombatState>, actor: CombatActor, target: CombatActor, amount: number): number {
  const defender = state.tactics!.actors[target.id]!;
  let dealt = amount;
  if (defender.stance === 'braced' && covers(target.position, defender.braceToward, actor.position) && gear(state, target.id).includes('shield-brace')) dealt = Math.max(1, Math.floor(amount / 4));
  if (defender.commitment?.action.type === 'withdraw') {
    dealt = Math.max(1, Math.ceil(dealt / 2));
    if (gear(state, target.id).includes('slip-past')) dealt = Math.max(1, Math.ceil(dealt / 2));
  }
  if (defender.commitment?.action.type === 'retreat') dealt = add(dealt, dealt);
  return dealt;
}

function damage(state: CombatState, actor: CombatActor, target: CombatActor, amount: number, lunge: boolean): void {
  const tactics = state.tactics!;
  const defender = tactics.actors[target.id]!;
  const dealt = receivedForce(state, actor, target, amount);
  // A blow turned on a set shield throws the striker off his line: he must recover before striking again.
  if (defender.stance === 'braced' && covers(target.position, defender.braceToward, actor.position) && gear(state, target.id).includes('shield-brace')) {
    tactics.actors[actor.id]!.readyTick = Math.max(tactics.actors[actor.id]!.readyTick, state.tick + STAGGER_TICKS);
  }
  if (dealt < amount) state.events.push({ type: 'blocked', tick: state.tick, actorId: target.id, targetId: actor.id, prevented: amount - dealt });
  target.hp = Math.max(0, target.hp - dealt);
  state.events.push({ type: 'hit', tick: state.tick, actorId: actor.id, targetId: target.id, damage: dealt });
  if (lunge && defender.commitment) {
    const interrupted = defender.commitment.action;
    defender.readyTick = Math.max(defender.readyTick, state.tick + 2);
    end(state, target, interrupted, 'interrupted', 'The spear interrupted the committed action.');
  }
  const person = tactics.continuity.persons[target.id];
  if (target.hp === 0) {
    target.status = 'dead'; target.activeAction = null;
    if (defender.commitment) end(state, target, defender.commitment.action, 'interrupted', 'Death ended the action permanently.');
    defender.order = null;
    stance(state, target, 'mobile');
    if (person) tactics.continuity = killPerson(tactics.continuity, target.id, `death:combat:${state.tick}:${target.id}`);
    const body = tactics.enemies[target.id];
    if (body?.kind === 'working-dead') tactics.continuity = changeRemains(tactics.continuity, body.remainsId, 'destroyed');
    if (target.id === state.pcId) state.pcQueue = [];
    state.events.push({ type: 'death', tick: state.tick, actorId: target.id });
  } else if (person) person.current.hp = target.hp;
}

/** The force of a committed attack or lunge as it leaves the striker, before the target's shield or footwork. */
function strike(state: Readonly<CombatState>, actor: CombatActor, commitment: Commitment): number {
  const action = commitment.action as Extract<TacticalAction, { type: 'attack' | 'lunge' }>;
  const own = state.tactics!.actors[actor.id]!, target = state.actors[action.targetId]!;
  const actions = gear(state, actor.id);
  let amount = actor.attackDamage;
  if (action.type === 'lunge' || actions.includes('cleave')) amount = add(amount, Math.ceil(amount / 2));
  // An arrow at range lands full; loosed point-blank it is half a blow.
  if (actions.includes('loose-arrow') && (metricDistance(actor.position, target.position) ?? 0) <= actor.attackRange) amount = Math.max(1, Math.floor(amount / 2));
  // A blow wound up short lands lighter.
  if (action.type === 'attack' && commitment.resolvesTick - commitment.startedTick < actor.attackWindupTicks) amount = CUT(amount);
  const armed = actions.some(value => ['thrust', 'cleave', 'loose-arrow', 'quick-cut'].includes(value));
  // Your own bare hands strike for half; hired and hostile fighters bring their authored blows.
  if (action.type === 'attack' && !armed && (actor.id === state.pcId || own.stance === 'holding')) amount = Math.max(1, Math.floor(amount / 2));
  // A strike from behind a set shield is short-armed.
  if (action.type === 'attack' && own.stance === 'braced') amount = BRACED(amount);
  return amount;
}

/** The force of the blow this actor has committed to, as the wind-up line reads it. */
export function committedForce(state: Readonly<CombatState>, actorId: string): number | null {
  const commitment = state.tactics?.actors[actorId]?.commitment;
  return commitment && (commitment.action.type === 'attack' || commitment.action.type === 'lunge') ? strike(state, state.actors[actorId]!, commitment) : null;
}

function resolve(state: CombatState, map: NavigationMap, actor: CombatActor, commitment: Commitment): void {
  const action = commitment.action;
  const own = state.tactics!.actors[actor.id]!;
  try { validateTacticalOrder(state, actor.id, action); }
  catch (error) { end(state, actor, action, 'unavailable', error instanceof Error ? error.message : 'Action unavailable'); return; }
  if ('destination' in action) {
    if (occupied(state, actor.id).some(p => sameCell(p, action.destination))) { end(state, actor, action, 'blocked', 'Someone occupies the destination.'); return; }
    actor.position = { ...action.destination };
    state.events.push({ type: 'moved', tick: state.tick, actorId: actor.id, position: actor.position });
  } else if (action.type === 'attack' || action.type === 'lunge') {
    const target = state.actors[action.targetId]!;
    // A lunge reaches a cell further; so does a strike from someone quicker on his feet than the one stepping away from it.
    const range = reach(state, actor, map) + (action.type === 'lunge' || actor.movePerTick > target.movePerTick ? map.cellSize : 0);
    const body = state.tactics!.enemies[actor.id];
    if ((metricDistance(actor.position, target.position) ?? Number.MAX_SAFE_INTEGER) > range || !hasLineOfSight(map, actor.position, target.position) ||
        (body?.kind === 'working-dead' && (metricDistance(body.anchor, target.position) ?? Number.MAX_SAFE_INTEGER) > body.radius)) {
      // A full swing that meets nothing throws the striker off balance, as a set shield does.
      if (action.type === 'attack') own.readyTick = Math.max(own.readyTick, state.tick + STAGGER_TICKS);
      end(state, actor, action, 'blocked', 'The target left the reach or line of sight.'); return;
    }
    damage(state, actor, target, strike(state, actor, commitment), action.type === 'lunge');
  } else if (action.type === 'brace') stance(state, actor, 'braced', action.toward);
  else if (action.type === 'hold') stance(state, actor, 'holding');
  else if (action.type === 'consume') {
    const spent = consumeItem(state.tactics!.continuity, actor.id, action.itemId);
    const healed = Math.min(actor.maxHp - actor.hp, TREAT_HEALS);
    actor.hp += healed;
    state.tactics!.continuity = spent.continuity;
    state.tactics!.continuity.persons[actor.id]!.current.hp = actor.hp;
    state.events.push({ type: 'consumed', tick: state.tick, actorId: actor.id, itemId: action.itemId, healed });
  }
  end(state, actor, action, 'resolved', 'The committed action completed.');
  if (['attack', 'lunge', 'consume'].includes(action.type) && canonical(own.order) === canonical(action)) own.order = null;
}

function dangerEvents(state: CombatState, map: NavigationMap): void {
  const tactics = state.tactics!;
  const pc = state.actors[state.pcId]!;
  const live = Object.values(state.actors).filter(a => a.status === 'alive' && !tactics.actors[a.id]!.escaped);
  // A fight with no foe left standing has nothing left to decide: danger is still reported, never paused for.
  const foesLeft = live.some(a => a.role === 'enemy');
  const emit = (trigger: DangerTrigger, actorId: string, targetId: string | null): void => {
    const autoPaused = tactics.dangerOptions.includes(trigger) && pc.status === 'alive' && foesLeft;
    state.events.push({ type: 'danger', tick: state.tick, actorId, targetId, trigger, autoPaused });
    if (autoPaused) state.paused = true;
  };
  // Sighting and targeting warn once per foe per fight: a foe stepping behind a
  // pillar or re-routing around a companion is not new danger, and re-warning on
  // every flicker froze play in pause loops.
  const seen = live.filter(a => a.role === 'enemy' && (metricDistance(pc.position, a.position) ?? Number.MAX_SAFE_INTEGER) <= 8000 && hasLineOfSight(map, pc.position, a.position)).map(a => a.id).sort();
  for (const id of seen) if (!tactics.sighted.includes(id)) emit('enemy-sighted', id, state.pcId);
  tactics.sighted = [...new Set([...tactics.sighted, ...seen])].sort();
  // A wound that halves you is a moment to choose again, when a dressing could answer it.
  const wounded = pc.status === 'alive' && pc.hp * 2 <= pc.maxHp && availableCombatOptions(state).consumables.length > 0;
  const hurt = [...live.filter(a => a.role === 'companion' && a.hp * 3 <= a.maxHp).map(a => a.id), ...wounded ? [state.pcId] : []].sort();
  for (const id of hurt) if (!tactics.badlyHurt.includes(id)) emit(id === state.pcId ? 'pc-wounded' : 'companion-badly-hurt', id, null);
  tactics.badlyHurt = hurt;
  // Targeted means a committed wind-up at the PC: the moment a response matters.
  // It warns once per foe, and again for each new wind-up whose answer is not
  // what you are already doing: the wind-up line's recommended answer
  // (windupAnswer), or any blow at all when you have chosen nothing. A blow your
  // standing order already meets runs on. A wind-up pauses only on the tick it
  // starts, so resuming cannot re-trigger it.
  const own = tactics.actors[state.pcId]!;
  const idle = !own.order && !own.commitment && !state.pcQueue.length;
  const targeted = live.filter(a => {
    const action = tactics.actors[a.id]!.commitment?.action;
    return a.role === 'enemy' && action && 'targetId' in action && action.targetId === state.pcId;
  }).map(a => a.id).sort();
  for (const id of targeted) if (!tactics.targetingPc.includes(id) || (tactics.actors[id]!.commitment!.startedTick === state.tick && (idle || ANSWERS.includes(windupAnswer(state, id)!)))) emit('pc-targeted', id, state.pcId);
  tactics.targetingPc = [...new Set([...tactics.targetingPc, ...targeted])].sort();
}

export function stepTactics(input: CombatState, map: NavigationMap, fallback: AutonomousDecision): CombatState {
  const state = structuredClone(input);
  state.tick = add(state.tick, 1);
  const tactics = state.tactics!;
  const snapshot = freeze(structuredClone(state));
  const ids = Object.keys(state.actors).sort();
  for (const id of ids) {
    const actor = state.actors[id]!;
    const own = tactics.actors[id]!;
    const policy = tactics.enemies[id];
    if (actor.status === 'dead' || own.escaped) continue;
    if (policy?.kind === 'working-dead' && tactics.continuity.remains[policy.remainsId]?.disposition !== 'animated') {
      if (own.commitment) end(state, actor, own.commitment.action, 'interrupted', 'The tether ended; the remains are still.');
      own.order = null; actor.hp = 0; actor.status = 'dead';
      state.events.push({ type: 'intention', tick: state.tick, actorId: id, goal: 'stilled', reason: 'The tether ended. The dead body is still; its remains keep their recorded disposition.', targetId: null });
      continue;
    }
    // A hireling may break off an unresolved swing to defend, get clear or bind a wound; recovery still binds.
    if (own.commitment?.action.type === 'attack' && own.commitment.resolvesTick > state.tick && tactics.companions[id]) {
      const urgent = decideTactics(snapshot, map, actor).goal;
      if (['withdraw', 'treat', 'defend'].includes(urgent)) {
        end(state, actor, own.commitment.action, 'interrupted', urgent === 'defend' ? 'Broken off to answer the incoming blow.' : 'Broken off to get clear.');
        own.readyTick = state.tick;
      }
    }
    if (own.commitment || state.tick < own.readyTick) continue;
    if (policy?.kind === 'thief' && own.fleeing && sameCell(actor.position, policy.escape)) { own.escaped = true; own.order = null; continue; }
    if (actor.role === 'pc') {
      const queued = state.pcQueue.shift();
      if (queued) own.order = queued.type === 'attack' ? { type: 'engage', targetId: queued.targetId } : queued;
    } else if (policy || tactics.companions[id]) {
      const decision = decideTactics(snapshot, map, actor);
      const key = canonical({ goal: decision.goal, reason: decision.reason, targetId: decision.targetId });
      if (key !== own.intention) {
        own.intention = key;
        state.events.push({ type: 'intention', tick: state.tick, actorId: id, goal: decision.goal, reason: decision.reason, targetId: decision.targetId });
      }
      if (decision.goal === 'flee') own.fleeing = true;
      own.order = decision.action;
    } else own.order = fallback(snapshot, id);
    const action = nextAction(state, map, actor);
    if (action) begin(state, map, actor, action);
  }
  // All actors telegraph before any resolution. Stable IDs break simultaneous ties.
  for (const id of ids) {
    const actor = state.actors[id]!;
    const commitment = tactics.actors[id]!.commitment;
    if (actor.status === 'alive' && commitment && state.tick >= commitment.resolvesTick) resolve(state, map, actor, commitment);
  }
  dangerEvents(state, map);
  return state;
}
